import { useEffect, useRef } from 'react';
import {
  ArrowClockwiseIcon,
  CaretDownIcon,
  CaretRightIcon,
  DesktopIcon,
} from '@phosphor-icons/react';
import { T } from 'gt-react';
import type { AgentRunSummary } from '@erp/rpc';
import { useSessionForAgentRun } from 'src/api/rpc/hooks/agent/useAgentQueries';
import type { RunTimeline } from 'src/components/agent-trace/useRunSpans';
import { AgentSessionViewer } from 'src/components/common/AgentSessionViewer';
import { Badge } from 'src/components/ui/badge';
import { Button } from 'src/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'src/components/ui/collapsible';
import { Skeleton } from 'src/components/ui/skeleton';
import { resolveRunSessionMedia } from '../agentPageRuns';

interface RunScreenContentProps {
  run: AgentRunSummary;
  timeline: RunTimeline | undefined;
  organizationId: string;
  focusRequest: { messageId: string } | null;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}

/** The selected run's desktop is discoverable even before the first trace arrives. */
export function AgentRunScreenPanel({
  open,
  onOpenChange,
  ...contentProps
}: RunScreenContentProps & {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const { focusRequest, run } = contentProps;
  // A screenshot action can be far down the trace. Bring its viewer into view
  // after the disclosure opens; a fresh request also handles repeated clicks.
  useEffect(() => {
    if (focusRequest && open) {
      panelRef.current?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [focusRequest, open]);

  return (
    <Collapsible
      ref={panelRef}
      open={open}
      onOpenChange={onOpenChange}
      className="min-w-0 rounded-lg border border-border"
    >
      <CollapsibleTrigger asChild>
        <Button variant="ghost" className="h-auto w-full justify-start p-3">
          {open ? (
            <CaretDownIcon aria-hidden />
          ) : (
            <CaretRightIcon aria-hidden />
          )}
          <DesktopIcon aria-hidden />
          <span className="min-w-0 whitespace-normal text-left">
            <T context="agent page collapsible run screen panel title">
              Screen &amp; Screenshots
            </T>
          </span>
          {run.status === 'running' && (
            <Badge variant="secondary">
              <T context="agent page screen panel badge for an active run">
                Live
              </T>
            </Badge>
          )}
          <span className="ml-auto">
            {open ? (
              <T context="collapse the agent run screen panel">Hide</T>
            ) : (
              <T context="expand the agent run screen panel">Show</T>
            )}
          </span>
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        {/* Unmount on collapse to stop session polling, streaming and the iframe. */}
        {open && <RunScreenContent {...contentProps} />}
      </CollapsibleContent>
    </Collapsible>
  );
}

function RunScreenContent({
  run,
  timeline,
  organizationId,
  focusRequest,
  isLoading,
  isError,
  onRetry,
}: RunScreenContentProps) {
  const isRunning = run.status === 'running';
  const session = useSessionForAgentRun({
    organizationId,
    agentRunId: isRunning ? run.id : undefined,
  });
  const media = resolveRunSessionMedia({
    timeline,
    liveSessionId: session.sessionId,
    isRunning,
  });

  if (media.hasMedia) {
    return (
      <AgentSessionViewer
        runId={run.id}
        runIsComplete={!isRunning}
        organizationId={organizationId}
        sessionId={media.sessionId}
        recordingDocumentId={media.recordingDocumentId}
        kernelTelemetryDocumentId={media.kernelTelemetryDocumentId}
        kernelTelemetrySummary={media.kernelTelemetrySummary}
        initialScreenshotUrl={media.initialScreenshotUrl}
        screenshotToolCalls={media.screenshotToolCalls}
        runCompletedAt={run.completedAt ?? null}
        hideMessageThread
        focusRequest={focusRequest}
      />
    );
  }

  if (isError || session.isError) {
    return (
      <div
        role="status"
        className="flex flex-col items-center gap-2 p-3 text-sm"
      >
        <T context="agent page screen media load error">
          Unable to load the screen and screenshots.
        </T>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            onRetry();
            if (isRunning) void session.refetch();
          }}
        >
          <ArrowClockwiseIcon aria-hidden />
          <T context="retry loading the agent run screen panel">Retry</T>
        </Button>
      </div>
    );
  }

  return <RunScreenPlaceholder isLoading={isLoading} isRunning={isRunning} />;
}

function RunScreenPlaceholder({
  isLoading,
  isRunning,
}: {
  isLoading: boolean;
  isRunning: boolean;
}) {
  if (isLoading) return <Skeleton className="m-3 h-32" />;
  return (
    <p role="status" className="p-3 text-center text-sm text-muted-foreground">
      {isRunning ? (
        <T context="agent page waiting for the run to open its live session">
          Connecting to session...
        </T>
      ) : (
        <T context="agent page selected run has no saved screen captures">
          No screenshots captured for this run.
        </T>
      )}
    </p>
  );
}
